// Global Instructions Rule Applied!
import React from 'react'
import { InputManager } from './InputManager/index.ts'
import type { FieldConfig, InputManagerProps } from './InputManager/types.ts'
import { useTabManagerContext } from '../app-layout/lib/TabManagerContext.tsx'
import { TABLE_CONTACT_ACCOUNTS, FIELD_KEY_SEPARATOR } from '../../state_manager/globals.ts'

// Introduced by / Sourced by are links between a contact and the ACCOUNT, not
// the deal or prospect whose form shows them (owner 2026-10-02). For an
// existing account every deal and prospect form - New drawer, Edit drawer,
// full form - shows them read-only and sends the user to the account form,
// where they are changed. Only a record that creates its account in the same
// save (New Company / New Investor) edits them in place.
const SOURCING_FIELD_KEYS: string[] = [
  `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}introduced_by`,
  `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}sourced_by`
]

const accountIdOf = (account: unknown): string | number | null => {
  if (typeof account === 'string') return account.trim() || null
  if (typeof account === 'number') return account
  if (account && typeof account === 'object') {
    const { value, id } = account as { value?: string | number; id?: string | number }
    return value || id || null
  }
  return null
}

/** True for the two team fields that belong to the account. */
export const isSourcingField = (field: FieldConfig): boolean => SOURCING_FIELD_KEYS.includes(field.key)

interface AccountSourcingFieldsProps {
  /** The form's team fields; the two sourcing fields are picked out and the rest ignored. */
  teamFields: FieldConfig[]
  /** The existing account, as the form holds it: a bare id or a select's
   *  {value, label} pair. No link is shown until it is known. */
  account: unknown
  /** What the form calls the account: 'company' on a deal, 'investor' on a prospect. */
  accountNoun: 'company' | 'investor'
  inputMode: InputManagerProps['inputMode']
  labelPosition: InputManagerProps['labelPosition']
  fieldWrapping?: boolean
  className?: string
}

/**
 * The account's Introduced by / Sourced by, read-only, with the line that
 * sends the user to the account form. The caller renders the rest of its team
 * fields (`teamFields.filter((field) => !isSourcingField(field))`) itself.
 */
export function AccountSourcingFields({
  teamFields,
  account,
  accountNoun,
  inputMode,
  labelPosition,
  fieldWrapping,
  className
}: AccountSourcingFieldsProps) {
  const { openFormInTab } = useTabManagerContext()
  const accountId = accountIdOf(account)

  return (
    <>
      <InputManager
        inputMode={inputMode}
        labelPosition={labelPosition}
        fields={teamFields.filter(isSourcingField).map((field) => ({ ...field, disabled: true }))}
        fieldWrapping={fieldWrapping}
        className={className}
      />
      {accountId && (
        <div data-debug='account-sourcing-contacts-link' className='text-xs text-gray-500 mb-2'>
          Introduced by and Sourced by are kept on the {accountNoun}.{' '}
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left text-xs'
            onClick={() => openFormInTab('companies', accountId)}
          >
            Change them on the {accountNoun} form
          </button>
        </div>
      )}
    </>
  )
}
